/*
 * ProfanityRegions — WHERE the censor goes. Pure: no DOM, no state, so the
 * page engine (profanity-censor-core.js) and the node stands/guards run the
 * very same code (tests/js/profanity-regions.test.mjs).
 *
 *   audio(pcm16k)                 → the recording as the edge search sees it
 *   hitSpan(ctx, i)               → where the SOUND of hit word i is (no pad)
 *   hitRegion(ctx, i, coverage)   → the censor for that hit
 *   layout(items, opts)           → what is actually rendered/exported
 *
 * ctx = { words, audio, detector, duration }.
 *
 * History of the numbers (all measured, see the tool memory
 * tool_profanity_remover_video.md, 24.09.2026):
 * - GigaAM word times are CTC emission frames: a word's start is the frame of
 *   its first letter, its end the frame of its last letter + 40 ms. Against
 *   ground truth (edge-tts word boundaries corrected to the energy onset,
 *   781 words) the start sits at +0…+40 ms and the end at −10 ms (medians),
 *   but both jitter by ±100 ms — so neither edge can be trusted alone.
 * - The END was the leaking side: 12% of mats kept ≥20 ms of their tail
 *   audible with the fixed +80 ms pad («на*уй — отчётливо слышится *уй»,
 *   Ангелина 23.09). Walking the end forward to the real pause, as the start
 *   has done since 08.08: clean tails 87.6% → 97.9%.
 * - Partial coverage used to take its fraction of the RAW emission span,
 *   skipping every correction the full path had (glued prefix, edge walks),
 *   so the muted window landed on the wrong part of the word. Now both paths
 *   share hitSpan(): coverage is a fraction of where the word really sounds.
 * - Neighbouring censors used to be MERGED when closer than 0.35 s; with
 *   coverage below 100% that muted the very edges the user asked to hear
 *   («хочу слышать начало первого мата и начало второго»), and at 100% it
 *   swallowed short innocent words between two mats (#1337). Only overlaps
 *   and slivers too short to carry a sound are joined now.
 * - 14.10.2026 — THE RECOGNISER'S TIME IS WHEN IT WROTE THE WORD, NOT WHEN
 *   THE WORD SOUNDS («не в том моменте цензурит мат. Это практически всегда
 *   именно слово бл*» — Ангелина, chat 640). Until now the search for a
 *   word's edges STARTED from that time and only ever moved outward from it
 *   — it assumed the time lies inside the word. Measured (edge-tts, 2 voices,
 *   truth = word boundaries on the energy onset): in fast or fluent speech
 *   the model writes a phrase's first word 100–300 ms BEFORE it sounds, in a
 *   burst that marks neither its start nor its end; «бля» — one syllable,
 *   three pieces — is the extreme: in fluent speech 35 % of them were
 *   written wholly before their own sound. The censor sat in the pause in
 *   front of the word: closed whole 15 % of the time at 100 % coverage, on
 *   the middle of the word 5 % of the time at 75 %.
 *   What the recogniser does give is the ORDER of the words. So the word is
 *   now LOCATED in the audio between its neighbours (locate()): its core is
 *   the sound standing above the local background, wherever in that stretch
 *   the model happened to write it; the recogniser's start counts only if
 *   sound joins it to that core; where speech runs on with no pause, the end
 *   is halfway to the next word's time. Four stands, before → after
 *   (100 % coverage: closed whole / 75 %: on the middle of the word):
 *       fluent speech                 70.8 → 94.2 %   /  19.2 → 50.8 %
 *         «бля» opening a phrase      15.0 → 82.5 %   /   5.0 → 67.5 %
 *       fast speech over a bed        94.0 → 97.1 %   /  64.0 → 83.5 %
 *       clean speech                  97.4 → 98.7 %   /  90.9 → 94.3 %
 *       real voices (Lingua Libre)    91.5 → 95.2 %   /  96.8 → 96.8 %
 *       the recogniser's time moved 300 ms early on purpose: 27–41 → 82–88 %
 *   Rejected by the same stands: the energy valley as a word boundary (17–30 %
 *   within 40 ms against 64 % for «halfway»: valleys sit INSIDE words); the
 *   next word's time minus a constant (53 %); a guard pad around the
 *   recogniser's time where the audio shows the edge (it muted 160–310 ms in
 *   front of the word); an extent floor tied to the word's own peak, and a
 *   «pause» proof for the recogniser's END (both cut the quiet last syllable
 *   of real long words: 91.5 → 53.6 % and → 71.5 %). See
 *   docs/word-sound-locate.md.
 *
 * 15.10.2026, the same chat: «слово распознало правильно, но его не
 *   зацензурило» — a quiet «нахуй.» before a loud «Ой» / a game sound. The
 *   loud sound set the island mark, the quiet word stayed under it, and the
 *   censor MOVED onto the loud sound as if the word had been written early.
 *   Now a move needs a sound within EARLY_REACH of the recogniser's start;
 *   otherwise a sound at the recogniser's time, above the ROOM, is the word,
 *   found at its own level. Quiet phrase next to a loud sound (100 % / 75 %,
 *   censors beside the word): 78.9 → 96.4 % / 76.8 → 96.1 %, 76 → 2; over a
 *   game bed 8–14 dB under the quiet voice: 65.3 → 94.0 % / 62.1 → 93.1 %,
 *   77 → 0. The four stands above: unchanged to the digit.
 *   …and on HER recording (41 min, 668 censors) that fix changed nothing
 *   where she complained: there the voice is no louder than the game bed,
 *   and the move still went to the shout. So no move beyond EARLY_REACH
 *   forward or LATE_GAP back at all, and the decay of a louder sound is not
 *   a word. Judged by the recogniser itself on her audio (does it still hear
 *   the mat once the censor is muted?) at the 12 places the rules disagree:
 *   heard 5 → 0 of 12 at 100 %, 7 → 4 at 75 %; the other 656 censors stay.
 */
(function (root) {
    'use strict';

    var SR = 16000;
    var FRAME = Math.floor(0.01 * SR);      // 10 ms analysis frame

    var C = {
        // Full-coverage safety margins around the word (CTC jitter).
        PAD_START: 0.12,
        PAD_END: 0.08,
        // How far an edge may follow continuous sound outward to reach the
        // pause that really bounds the word.
        EDGE_MAX: 0.40,
        // …and for a word whose start the ASR could only bound from above
        // (`ls`: its first letter was swallowed by the window-start prior,
        // see gigaam-asr-worker.js), where the onset can be further back.
        LEAD_EDGE_MAX: 1.20,
        // Past a found pause, a small margin into the silence.
        PAD_MIN_EDGE: 0.04,
        // A silence this long is a pause between words; shorter ones are
        // stop-consonant closures inside a word (see edge()).
        PAUSE_MIN_S: 0.06,
        // Median emission offsets when no pause bounds the word (connected
        // speech): the start is late, the end slightly early.
        START_LAG: 0.02,
        END_LEAD: 0.01,
        // Words shorter than this are censored whole at any coverage: a 40%
        // mute of a 200 ms word is 80 ms — nothing is hidden.
        MIN_PARTIAL_S: 0.22,
        // A gap shorter than this between two censors cannot carry a sound
        // (two GATE_FADE ramps of the export graph never reach unity inside
        // it) — joined, so the export does not click.
        BRIDGE_S: 0.06,
        // A split-off prefix («На» + «хуй») is glued when this close.
        GLUE_MAX_GAP: 0.30,

        /* ---- locate(): where the word SOUNDS ---- */
        // The local picture is ±LOCAL_S around the word. Speech must stand
        // MIN_CONTRAST above the background there for the audio to be a
        // witness at all (else: the walks from the recogniser's time).
        LOCAL_S: 2.0,
        MIN_CONTRAST: 3.0,
        // A sound counts as an island above the level halfway (in dB) between
        // the room and the speech, and never lower than REL_MIN of the speech
        // (in a silent room «halfway» is where breath and room tone are still
        // «sound»: every word then ran on into its neighbour).
        REL_MIN: 0.15,
        // Its EXTENT is followed outward while the sound stands above the room
        // by LOW_BG and above LOW_REL of the level at the edge it left — a
        // vowel's decay, a quiet syllable (the walks' own 12 % since 24.09).
        LOW_BG: 2.5,
        LOW_REL: 0.12,
        // How far past the recogniser's end the word's sound is looked for,
        // and how far past the NEXT word's time (that one can be early too).
        FWD_MAX: 0.60,
        HI_REACH: 0.30,
        HI_SLACK: 0.05,
        SHIFT_CARRY: 0.5,
        // A sound shorter than this is not a word (a click, a tap) — frames.
        MIN_ISLAND_F: 5,
        // How long before its sound a word can be written (measured 100–300 ms
        // for the first word of a phrase; the stand that moves every time
        // 300 ms early still holds at this reach). A sound beginning farther
        // after the recogniser's start than this is not that word written
        // early — a quiet word at its time is (chat 640, 15.10.2026).
        EARLY_REACH: 0.45,
        // A word written AFTER its sound (a sentence-final «нахуй.»: the
        // period comes late) has that sound right before the recogniser's
        // time — her recording: gaps 0.02–0.10 s. A sound ending farther
        // back is another word.
        LATE_GAP: 0.20,
        // What sounds at the recogniser's time is the decay of a louder
        // sound, not a word, when that sound stands this many times (in
        // level; 4 = 12 dB) over it.
        TAIL_RATIO: 4,
        // Fluent speech: the boundary is halfway to the next word, but a
        // word does not run on for longer than this past its last piece
        // (the recogniser may have missed what follows).
        MIN_WORD_S: 0.05,
        FLUENT_MAX: 0.25,
        // Margins around an edge the audio SHOWS (a pause): the full censor's,
        // and the one of a short word censored whole at partial coverage.
        PAD_SOUND: 0.06,
        PAD_SHORT: 0.02
    };

    /* ASR sometimes emits a swear word as TWO tokens — measured: «нахуй»
     * came back as «На» + «хуй», so only the second half was flagged and
     * the first 76 ms stayed audible. Glue the neighbour in when it is one
     * of the short prefixes Russian fuses with mat, it sits right next to
     * the hit, and the two TOGETHER are what the detector calls profanity.
     * The list is what keeps this safe: «это»+«хуйня» also matches a
     * substring rule, and swallowing «это» would censor an innocent word. */
    var GLUE_PREFIXES = { 'на': 1, 'по': 1, 'за': 1, 'до': 1, 'от': 1, 'под': 1,
        'ни': 1, 'не': 1, 'об': 1, 'пере': 1, 'при': 1, 'нах': 1, 'пох': 1, 'о': 1, 'у': 1 };

    function audio(pcm) {
        var n = pcm ? pcm.length : 0;
        function frameRms(idx) {
            if (idx < 0 || idx + FRAME > n) return 0;
            var sum = 0;
            for (var j = idx; j < idx + FRAME; j += 2) { var v = pcm[j] / 32768; sum += v * v; }
            return Math.sqrt(sum / (FRAME / 2));
        }
        // Room-noise level: the 15th percentile of frame energy, sampled
        // every 8th frame — on an hour of audio the exact value moves by
        // nothing that matters.
        var levels = [];
        for (var f = 0; f + FRAME <= n; f += FRAME * 8) levels.push(frameRms(f));
        levels.sort(function (a, b) { return a - b; });
        var noiseFloor = levels.length ? (levels[Math.floor(levels.length * 0.15)] || 0) : 0;

        /* Where does the SOUND of a word really end in direction `dir`?
         * Walk the 16 kHz PCM outward from `t` while it is still loud, stop at
         * the first pause, never past `limitS` or the neighbour `boundT`.
         * A pause must last 2 frames: a stop consonant inside a word dips for
         * one, and stopping there is what left «на» of «нахуй» audible.
         * Returns {t, pause}: only a FOUND pause moves the edge — running out
         * of budget in fluent speech is not a word boundary. */
        function edge(t, dir, limitS, boundT) {
            if (!n || !limitS) return { t: t, pause: false };
            var idx = Math.round(t * SR);
            var ref = Math.max(frameRms(idx), frameRms(idx - dir * FRAME));
            if (!ref) return { t: t, pause: false };
            var floorE = Math.max(noiseFloor * 2.5, ref * 0.12);
            var limitIdx = Math.round((dir < 0 ? Math.max(t - limitS, boundT) : Math.min(t + limitS, boundT)) * SR);
            var minRun = Math.max(2, Math.round(C.PAUSE_MIN_S / 0.01));
            var i = idx, quiet = 0, runAt = -1;
            while (dir < 0 ? (i - FRAME >= limitIdx) : (i + FRAME <= limitIdx)) {
                var probe = dir < 0 ? i - FRAME : i;
                if (frameRms(probe) < floorE) {
                    if (quiet === 0) runAt = i;                  // the word's sound ends here
                    if (++quiet >= minRun) return { t: runAt / SR, pause: true };
                } else {
                    /* Sound again after a SHORT silence: that was a stop
                     * consonant's closure INSIDE the word («су-к-а»: 20–40 ms of
                     * nothing before the burst), not its end. Stopping there
                     * left the final vowel audible — the tails that still
                     * leaked after the right walk arrived (Ангелина 23.09). */
                    quiet = 0;
                }
                i += dir * FRAME;
            }
            // Silence that runs into the budget/neighbour is still a boundary.
            if (quiet >= 2) return { t: runAt / SR, pause: true };
            return { t: t, pause: false };
        }

        // Snap to the nearest zero crossing within 8 ms (no click at the cut).
        function snapZero(t) {
            if (!n) return t;
            var idx = Math.round(t * SR);
            var R = Math.floor(0.008 * SR);
            for (var d = 0; d < R; d++) {
                var a = idx + d, b = idx - d;
                if (a + 1 < n && pcm[a] * pcm[a + 1] <= 0) return (a + 0.5) / SR;
                if (b > 0 && pcm[b - 1] * pcm[b] <= 0) return (b - 0.5) / SR;
            }
            return t;
        }

        /* RMS of ±ctxS around [a,b], the span itself excluded — the loudness
         * of the speech a censor sits in (ProfanitySound's auto level). */
        function contextRms(a, b, ctxS) {
            if (!n) return 0;
            var s = Math.max(0, Math.floor((a - ctxS) * SR)), e = Math.min(n, Math.ceil((b + ctxS) * SR));
            var rs = Math.floor(a * SR), re = Math.ceil(b * SR);
            var sum = 0, cnt = 0;
            for (var i = s; i < e; i += 2) {
                if (i >= rs && i < re) { i = re - 2; continue; }
                var v = pcm[i] / 32768; sum += v * v; cnt++;
            }
            return cnt ? Math.sqrt(sum / cnt) : 0;
        }

        /* Frame levels (10 ms) of [t0, t1): { i0, v } — v[k] is frame i0 + k. */
        function frameLevels(t0, t1) {
            var i0 = Math.max(0, Math.floor(t0 * 100)), i1 = Math.min(Math.floor(n / FRAME), Math.ceil(t1 * 100));
            var v = new Float32Array(Math.max(0, i1 - i0));
            for (var k = 0; k < v.length; k++) v[k] = frameRms((i0 + k) * FRAME);
            return { i0: i0, v: v };
        }

        return { frameRms: frameRms, edge: edge, snapZero: snapZero, contextRms: contextRms, levels: frameLevels, noiseFloor: noiseFloor, duration: n / SR };
    }

    function gluedPrev(ctx, i) {
        var words = ctx.words, w = words[i], prev = words[i - 1], det = ctx.detector;
        if (!prev || !det || ctx.noGlue) return -1;
        if (w.start - prev.end > C.GLUE_MAX_GAP) return -1;
        var pn = det.normalizeToken(prev.text);
        if (!pn || !GLUE_PREFIXES[pn]) return -1;
        try { if (!det.scanToken(prev.text + w.text)) return -1; } catch (e) { return -1; }
        return i - 1;
    }

    function pctl(sorted, p) { return sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] : 0; }

    /* WHERE THE WORD SOUNDS — asked of the audio, not of the recogniser's clock.
     *
     * The recogniser's time is when it WROTE the word (see the header). What
     * it gives reliably is the ORDER: the word sounds after the previous word
     * was written (lo) and about where the next one is (hi). In that stretch
     * the word is the sound standing above the local background — an island.
     *
     * Returns null when the audio cannot say (speech does not stand out here,
     * or nothing sounds in reach), else
     *   { start, end, startFound, endFound, moved }
     * startFound / endFound — that edge is a pause the audio shows, not the
     *     search bound (fluent speech runs on into the neighbour);
     * moved — nothing sounds where the word was written: it is the nearest
     *     sound after (+1, written early) or before (−1). */
    function locate(ctx, first, w, before, next, dur) {
        var a = ctx.audio;
        if (!a || !a.levels || ctx.noLocate) return null;
        var asrStart = first.start, asrEnd = Math.max(w.end, first.start + 0.04);
        var lo = before ? Math.min(before.end, asrStart) : 0;
        var hi = next ? Math.max(next.start, asrEnd) : dur;
        var L = a.levels(asrStart - C.LOCAL_S, asrEnd + C.LOCAL_S);
        if (L.v.length < 20) return null;
        var sorted = Array.prototype.slice.call(L.v).sort(function (x, y) { return x - y; });
        var bg = Math.max(pctl(sorted, 0.10), a.noiseFloor * 0.5, 1e-5), sp = pctl(sorted, 0.95);
        if (!(sp > bg * C.MIN_CONTRAST)) return null;
        var thr = Math.max(Math.sqrt(bg * sp), sp * C.REL_MIN);
        var from = Math.max(lo, asrStart - (first.ls ? C.LEAD_EDGE_MAX : C.EDGE_MAX));
        var to = Math.min(dur, hi + C.HI_REACH, asrEnd + C.FWD_MAX);
        var f0 = Math.floor(from * 100), f1 = Math.ceil(to * 100);
        function lvl(f) { var k = f - L.i0; return (k >= 0 && k < L.v.length) ? L.v[k] : a.frameRms(f * FRAME); }
        // islands: runs above the mark; a stop's closure is not a pause
        var isl = [], cur = null, gapMax = Math.round(C.PAUSE_MIN_S * 100);
        for (var f = f0; f < f1; f++) {
            if (lvl(f) > thr) {
                if (cur && f - cur.e <= gapMax) cur.e = f + 1;
                else { cur = { s: f, e: f + 1 }; isl.push(cur); }
            }
        }
        if (!isl.length) return null;
        var as = Math.floor((asrStart - 0.03) * 100), ae = Math.ceil((asrEnd + 0.03) * 100), hiF = Math.round(hi * 100);
        var pick = isl.filter(function (x) { return x.e > as && x.s < ae && (x.e - x.s) >= 3; });
        var moved = 0;
        /* Nothing above the mark where the word was written. A word written
         * EARLY has its sound within EARLY_REACH after that time; failing
         * that, the question is whether something sounds AT the time — judged
         * against the room, not against the loudest sound within two seconds.
         * A quiet speaker before a loud shout or a game sound stays under a
         * mark that sound set; moving to «the next sound» put the censor on
         * the shout and left the word heard (chat 640, 15.10.2026: «нахуй.»,
         * «пизду.»; 76 of 192 such censors beside the word on the stand). */
        var nearF = Math.round((asrStart + C.EARLY_REACH) * 100);
        var early = !pick.length && isl.some(function (x) { return x.s >= ae - 1 && x.s < hiF && x.s <= nearF && (x.e - x.s) >= C.MIN_ISLAND_F; });
        if (!pick.length && !early) {
            var spW = 0;
            for (var fw = Math.max(f0, as); fw < Math.min(f1, ae); fw++) spW = Math.max(spW, lvl(fw));
            if (spW > bg * C.MIN_CONTRAST) {
                // the word's own islands, at the mark its own level sets
                var thrW = Math.max(Math.sqrt(bg * spW), spW * C.REL_MIN), curW = null, islW = [];
                for (var fz = f0; fz < f1; fz++) {
                    if (lvl(fz) > thrW) {
                        if (curW && fz - curW.e <= gapMax) curW.e = fz + 1;
                        else { curW = { s: fz, e: fz + 1 }; islW.push(curW); }
                    }
                }
                pick = islW.filter(function (x) { return x.e > as && x.s < ae && (x.e - x.s) >= 3; });
                // …unless it is the DECAY of a louder sound: an island that rises
                // above the mark and stands TAIL_RATIO over what sounds at the
                // time is the word before — or this word, written after it
                // («без экспы нахуй.», «я в ахуе.» on her recording)
                for (var pw = 0; pw < pick.length; pw++) {
                    var pk = 0;
                    for (var fy = pick[pw].s; fy < pick[pw].e; fy++) pk = Math.max(pk, lvl(fy));
                    if (pk > thr && spW * C.TAIL_RATIO < pk) { pick = []; break; }
                }
            }
        }
        if (!pick.length) {
            /* Written where nothing of its own sounds: its sound is the first
             * one after within EARLY_REACH (written early), else the one ending
             * within LATE_GAP before (written late). Nothing that close: the
             * audio does not say — the walks from the recogniser's time. On
             * her 41 minutes the right moves were +0.11…+0.38 s and −0.25…
             * −0.32 s (gap ≤ 0.10); the only two farther ones were her two
             * complaints (+0.66, +0.73). */
            var after = isl.filter(function (x) { return x.s >= ae - 1 && x.s < hiF && x.s <= nearF && (x.e - x.s) >= C.MIN_ISLAND_F; });
            var prior = isl.filter(function (x) { return x.e <= as + 1 && as - x.e <= Math.round(C.LATE_GAP * 100) && (x.e - x.s) >= C.MIN_ISLAND_F; });
            if (after.length) { pick = [after[0]]; moved = 1; }
            else if (prior.length) { pick = [prior[prior.length - 1]]; moved = -1; }
            else return null;
        }
        var s = pick[0].s, e = pick[pick.length - 1].e;
        /* The core is found; its EXTENT is followed outward to the first
         * pause, judged against the level AT the edge — a quiet last syllable
         * is still the word («долбоёб-ство», «съебать-ся») — and against the
         * room: in a loud bed the room is where the word stops. (A floor tied
         * to the word's own PEAK cut those syllables off: on real voices 91.5 %
         * of words closed whole fell to 53.6 %.) */
        var roomF = bg * C.LOW_BG;
        function follow(fEdge, dir, fBound) {
            var inside = fEdge - dir;
            var ref = Math.max(lvl(inside), lvl(inside - dir));
            var floorE = Math.max(roomF, ref * C.LOW_REL);
            var q = 0, last = inside;
            for (var f2 = fEdge; dir > 0 ? f2 < fBound : f2 >= fBound; f2 += dir) {
                if (lvl(f2) > floorE) { last = f2; q = 0; }
                else if (++q > gapMax) return { f: last, pause: true };
            }
            return { f: last, pause: q >= 2 };          // silence running into the bound is a boundary too
        }
        /* Is the recogniser's START inside the word? It is, when sound joins it
         * to the core: no stretch at the room's own level, as long as a pause,
         * lies between them. Then the search goes on from that time, against
         * the level there — the quiet first syllable it began in («с-ху-ЯЧИТЬ»).
         * A start the room separates from the core was written early.
         * Its END needs no such proof: the model finishes writing a word before
         * it finishes sounding, never far after — and asking for one read a
         * geminate's 100 ms closure («-ться») as the room (95.2 → 71.5 %). */
        function joined(fa, fb) {
            var q = 0;
            for (var f3 = fa; f3 < fb; f3++) { if (lvl(f3) > roomF) q = 0; else if (++q > gapMax) return false; }
            return true;
        }
        var sA = s, eA = e;
        if (!moved) {
            var aS = Math.max(f0, Math.floor(asrStart * 100)), aE = Math.min(f1, Math.ceil(asrEnd * 100));
            if (aS < s && joined(aS, s)) sA = aS;
            if (aE > e) eA = aE;
        }
        var Lw = follow(sA - 1, -1, f0), Rw = follow(eA, +1, f1);
        s = Lw.f; e = Rw.f + 1;
        var startFound = Lw.pause;
        // written early by `shift`: after a pause the next word tends to be
        // early by about half as much (measured slope 0.5), so a pause found
        // that far past its time still ends THIS word
        var shift = startFound ? Math.max(0, s / 100 - asrStart) : 0;
        var cap = next ? hi + C.HI_SLACK + shift * C.SHIFT_CARRY : dur;
        var endFound = Rw.pause && e / 100 <= cap + 1e-9;
        return { start: Math.max(from, s / 100), end: Math.min(to, e / 100), startFound: startFound, endFound: endFound, moved: moved };
    }

    /* Where the sound of hit `i` is, without any safety margin.
     *   start/end   best estimate of the word's own sound
     *   startFound  / endFound: that edge is a pause the audio shows
     *   asrStart    the (glue-aware) ASR start the full-coverage pad keys on
     *   first       index of the first word the hit covers (a glued prefix)
     *   label       the text the hit reads as */
    function hitSpan(ctx, i) {
        var words = ctx.words, w = words[i], a = ctx.audio;
        var dur = ctx.duration || (a && a.duration) || 1e9;
        var g = gluedPrev(ctx, i);
        var first = g >= 0 ? words[g] : w;
        var before = words[(g >= 0 ? g : i) - 1];
        var next = words[i + 1];
        var asrStart = first.start;
        var lo = before ? before.end : 0;
        var hi = next ? next.start : dur;
        var start = asrStart - C.START_LAG, end = w.end + C.END_LEAD;
        var startFound = false, endFound = false, fluentEnd = false;
        var loc = a ? locate(ctx, first, w, before, next, dur) : null;
        if (loc) {
            if (loc.startFound || loc.moved) { start = loc.start; startFound = loc.startFound; }
            if (loc.endFound) { end = loc.end; endFound = true; }
            else if (next) {
                /* Speech runs on into the next word. The model finishes
                 * WRITING a word before it finishes sounding, and begins
                 * writing the next after that one has begun — so the boundary
                 * lies between the two writings; halfway is the unbiased
                 * guess (within 40 ms for 64 % of fluent boundaries on the
                 * stand; «where this word was last written», the rule until
                 * 14.10.2026, 8 %). */
                var base = Math.max(w.end, start + C.MIN_WORD_S);
                end = hi > base ? Math.min((base + hi) / 2, base + C.FLUENT_MAX) : base;
                fluentEnd = true;
            } else if (loc.moved) {
                end = loc.end;
            }
        } else if (a) {
            // the audio cannot say where the word is: follow continuous sound
            // outward from the recogniser's time to the pauses, as before
            var L = a.edge(asrStart, -1, first.ls ? C.LEAD_EDGE_MAX : C.EDGE_MAX, lo);
            if (L.pause) start = L.t;
            var R = a.edge(w.end, +1, C.EDGE_MAX, hi);
            if (R.pause) end = Math.max(end, R.t);
        }
        if (end < start + 0.04) end = start + 0.04;
        return {
            start: Math.max(0, start), end: Math.min(dur, end), asrStart: asrStart,
            startFound: startFound, endFound: endFound, fluentEnd: fluentEnd, moved: loc ? loc.moved : 0,
            first: g >= 0 ? g : i,
            label: g >= 0 ? words[g].text + w.text : w.text
        };
    }

    /* The censor for hit `i` at `coverage` % of the word (40..100). */
    function hitRegion(ctx, i, coverage) {
        var sp = hitSpan(ctx, i);
        var dur = ctx.duration || 1e9, w = ctx.words[i];
        var cov = Math.max(1, Math.min(100, coverage || 100));
        var len = sp.end - sp.start, s, e;
        if (cov < 100 && len > C.MIN_PARTIAL_S) {
            var k = (100 - cov) / 200;          // kept on EACH side
            s = sp.start + len * k;
            e = sp.end - len * k;
        } else {
            /* The whole word. Where the audio SHOWS an edge (a pause), the
             * censor is the sound plus a margin — the recogniser's time has
             * no say there: padding around a time written 300 ms early muted
             * 300 ms of whatever stood before the word. Where it does not
             * (fluent speech), the fixed pad around the recogniser's time
             * stays as the guarantee it has always been. A short word
             * censored whole at partial coverage gets the word, not the
             * margin of a full censor. */
            var pad = cov < 100 ? C.PAD_SHORT : C.PAD_SOUND;
            s = sp.startFound ? sp.start - pad
                : Math.min(sp.asrStart - C.PAD_START, sp.start - C.PAD_MIN_EDGE);
            e = sp.endFound ? sp.end + pad
                : sp.fluentEnd ? Math.max(w.end + C.END_LEAD, sp.end + (cov < 100 ? 0 : C.PAD_MIN_EDGE))
                : Math.max(w.end + C.PAD_END, sp.end + C.PAD_MIN_EDGE);
            if (e < s + 0.08) e = s + 0.08;
        }
        // how the place was found, for the field's telemetry: m — moved to the
        // sound, s / e — that edge is a pause the audio shows, f — fluent end
        var how = (sp.moved ? 'm' : '') + (sp.startFound ? 's' : '') + (sp.endFound ? 'e' : '') + (sp.fluentEnd ? 'f' : '');
        return { start: Math.max(0, s), end: Math.min(dur, e), label: sp.label, first: sp.first, how: how };
    }

    /* Items (one per censor, any order) → the regions that are rendered.
     * Only overlaps and gaps under `bridge` are joined. `cap` (the export
     * graph's ceiling) widens the bridge until the count fits — the caller
     * reports that, because it means speech between censors got muted.
     * `snap` (optional) moves each final edge to a zero crossing. */
    function layout(items, opts) {
        opts = opts || {};
        var bridge = typeof opts.bridge === 'number' ? opts.bridge : C.BRIDGE_S;
        var cap = opts.cap || Infinity;
        var sorted = items.filter(function (r) { return r && r.end > r.start; })
            .map(function (r) { return { start: r.start, end: r.end }; })
            .sort(function (a, b) { return a.start - b.start || a.end - b.end; });
        var merged, g = bridge;
        for (;;) {
            merged = [];
            for (var i = 0; i < sorted.length; i++) {
                var r = sorted[i], last = merged[merged.length - 1];
                if (last && r.start - last.end < g) last.end = Math.max(last.end, r.end);
                else merged.push({ start: r.start, end: r.end });
            }
            if (merged.length <= cap) break;
            g += 0.3;
        }
        if (opts.snap) merged.forEach(function (r) { r.start = opts.snap(r.start); r.end = opts.snap(r.end); });
        return { regions: merged, bridge: g, widened: g > bridge };
    }

    var api = { C: C, audio: audio, hitSpan: hitSpan, hitRegion: hitRegion, layout: layout, GLUE_PREFIXES: GLUE_PREFIXES };
    root.ProfanityRegions = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof self !== 'undefined' ? self : this);
